import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import ConversationView from './components/ConversationView';
import AdminPanel from './components/AdminPanel';
import CustomizeFlow from './components/CustomizeFlow';
import MainChat from './components/MainChat';
import AdminOnboardingPage from './pages/AdminOnboardingPage';
import CustomerDashboardPage from './pages/CustomerDashboardPage';
import CustomerWorkspacePage from './pages/CustomerWorkspacePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SetupProfileReviewPage from './pages/SetupProfileReviewPage';
import SetupPromptReviewPage from './pages/SetupPromptReviewPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AvatarSharePage from './pages/AvatarSharePage';
import DebugPage from './pages/DebugPage';
import {
  DEFAULT_PUBLIC_CUSTOMER_SLUG,
  DEFAULT_PUBLIC_AGENT_SLUG,
  DEFAULT_PUBLIC_WORKSPACE_SLUG,
  SALLY_PUBLIC_DEMO_URL,
  resolvePublicAgentRouteScope,
  sanitizePublicSlug,
  shouldRedirectLegacySallyRoot,
} from './utils/agentRouting';
import { hasCompletedOnboarding } from './utils/workspace';
import './App.css';

interface RootRouteProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const RootRoute: React.FC<RootRouteProps> = ({ isDarkMode, onToggleTheme }) => {
  const shouldRedirectToPublicSallyLanding = shouldRedirectLegacySallyRoot(
    window.location.hostname,
    window.location.pathname,
  );

  useEffect(() => {
    if (shouldRedirectToPublicSallyLanding) {
      window.location.replace(SALLY_PUBLIC_DEMO_URL);
    }
  }, [shouldRedirectToPublicSallyLanding]);

  if (shouldRedirectToPublicSallyLanding) {
    return null;
  }

  return <MainChat isDarkMode={isDarkMode} onToggleTheme={onToggleTheme} />;
};

interface WorkspaceRouteProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const readStoredAdminSession = (): any => {
  try {
    return JSON.parse(window.sessionStorage.getItem('admin_session') || 'null');
  } catch {
    return null;
  }
};

export const getEndCustomerAgentUrl = (session: any): string | null => {
  if (!session) return null;
  const memberships = Array.isArray(session?.customer_memberships) ? session.customer_memberships : [];
  const isEndCustomer = session?.role === 'customeruser'
    || (
      memberships.some((membership: any) => membership?.role === 'customeruser')
      && !memberships.some((membership: any) => membership?.role === 'customeradmin')
    );
  if (!isEndCustomer) return null;

  const grants = Array.isArray(session?.agent_access) ? session.agent_access : [];
  const firstGrant = grants.find((grant: any) => (
    grant?.status === 'active'
    && sanitizePublicSlug(grant?.customer_slug, '')
    && sanitizePublicSlug(grant?.agent_slug, '')
  ));
  if (!firstGrant) return '/';

  return `/agent/${sanitizePublicSlug(firstGrant.customer_slug, '')}/${sanitizePublicSlug(firstGrant.agent_slug, '')}`;
};

const AdminAreaRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const hasSession = Boolean(window.sessionStorage.getItem('admin_token'));
  const endCustomerAgentUrl = hasSession ? getEndCustomerAgentUrl(readStoredAdminSession()) : null;
  return endCustomerAgentUrl ? <Navigate to={endCustomerAgentUrl} replace /> : children;
};

const sessionCanAccessAgent = (
  session: any,
  customerSlug: string,
  workspaceSlug: string,
  agentSlug: string,
): boolean => {
  if (!session) return false;
  const isSuperadmin = Boolean(session?.is_superadmin)
    || session?.role === 'superadmin'
    || session?.platform_role === 'owner'
    || session?.platform_role === 'superadmin';
  if (isSuperadmin) return true;

  const memberships = Array.isArray(session?.customer_memberships) ? session.customer_memberships : [];
  const hasCustomerAdminAccess = memberships.some((membership: any) => (
    membership?.role === 'customeradmin'
    && membership?.status === 'active'
    && sanitizePublicSlug(membership?.customer_slug, '') === customerSlug
  ));
  if (hasCustomerAdminAccess) return true;

  const agentAccess = Array.isArray(session?.agent_access) ? session.agent_access : [];
  return agentAccess.some((grant: any) => (
    grant?.status === 'active'
    && sanitizePublicSlug(grant?.customer_slug, '') === customerSlug
    && sanitizePublicSlug(grant?.workspace_slug, DEFAULT_PUBLIC_WORKSPACE_SLUG) === workspaceSlug
    && sanitizePublicSlug(grant?.agent_slug, '') === agentSlug
  ));
};

const WorkspaceRoute: React.FC<WorkspaceRouteProps> = ({ isDarkMode, onToggleTheme }) => {
  if (!hasCompletedOnboarding()) {
    return <Navigate to="/setup" replace />;
  }

  return <CustomerWorkspacePage isDarkMode={isDarkMode} onToggleTheme={onToggleTheme} />;
};

const PublicAgentRoute: React.FC<WorkspaceRouteProps> = ({ isDarkMode, onToggleTheme }) => {
  const { agentSlug } = useParams<{ agentSlug: string }>();
  const location = useLocation();
  const scope = resolvePublicAgentRouteScope(
    window.location.hostname,
    window.location.pathname,
    agentSlug,
  );

  if (!scope) {
    return <Navigate to="/" replace />;
  }

  const isPublicPlatformDemo = scope.customerSlug === DEFAULT_PUBLIC_CUSTOMER_SLUG
    && scope.agentSlug === DEFAULT_PUBLIC_AGENT_SLUG;
  if (!isPublicPlatformDemo) {
    const token = window.sessionStorage.getItem('admin_token');
    const session = readStoredAdminSession();
    if (!token || !sessionCanAccessAgent(session, scope.customerSlug, scope.workspaceSlug, scope.agentSlug)) {
      const redirect = `${location.pathname}${location.search}`;
      return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
    }
  }

  return (
    <CustomerWorkspacePage
      isDarkMode={isDarkMode}
      onToggleTheme={onToggleTheme}
      agentRoutePresentation
      routeCustomerSlug={scope.customerSlug}
      routeWorkspaceSlug={scope.workspaceSlug}
      routeAgentSlug={scope.agentSlug}
    />
  );
};

const CustomerAgentRoute: React.FC<WorkspaceRouteProps> = ({ isDarkMode, onToggleTheme }) => {
  const { customerSlug, agentSlug } = useParams<{ customerSlug: string; agentSlug: string }>();
  const location = useLocation();
  const safeCustomerSlug = sanitizePublicSlug(customerSlug, '');

  if (!safeCustomerSlug) {
    return <Navigate to="/" replace />;
  }

  const safeAgentSlug = sanitizePublicSlug(agentSlug, DEFAULT_PUBLIC_AGENT_SLUG);
  const isPublicPlatformDemo = safeCustomerSlug === DEFAULT_PUBLIC_CUSTOMER_SLUG
    && safeAgentSlug === DEFAULT_PUBLIC_AGENT_SLUG;
  if (!isPublicPlatformDemo) {
    const token = window.sessionStorage.getItem('admin_token');
    const session = readStoredAdminSession();
    if (!token || !sessionCanAccessAgent(
      session,
      safeCustomerSlug,
      DEFAULT_PUBLIC_WORKSPACE_SLUG,
      safeAgentSlug,
    )) {
      const redirect = `/agent/${safeCustomerSlug}/${safeAgentSlug}${location.search}`;
      return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
    }
  }

  return (
    <CustomerWorkspacePage
      isDarkMode={isDarkMode}
      onToggleTheme={onToggleTheme}
      agentRoutePresentation
      routeCustomerSlug={safeCustomerSlug}
      routeWorkspaceSlug={DEFAULT_PUBLIC_WORKSPACE_SLUG}
      routeAgentSlug={safeAgentSlug}
    />
  );
};

const ConversationRoute: React.FC<WorkspaceRouteProps> = ({ isDarkMode, onToggleTheme }) => {
  const location = useLocation();
  if (!window.sessionStorage.getItem('admin_token')) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }
  return <ConversationView isDarkMode={isDarkMode} onToggleTheme={onToggleTheme} />;
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div 
      className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'
      }`}
      data-theme={isDarkMode ? 'dark' : 'light'}
    >
      <Routes>
        <Route 
          path="/" 
          element={<RootRoute isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />} 
        />
        <Route
          path="/setup"
          element={<CustomizeFlow isDarkMode={isDarkMode} />}
        />
        <Route
          path="/verify-email"
          element={<VerifyEmailPage isDarkMode={isDarkMode} />}
        />
        <Route
          path="/share/:token"
          element={<AvatarSharePage isDarkMode={isDarkMode} />}
        />
        <Route
          path="/setup/prompt/:customerSlug/:workspaceSlug/:agentSlug"
          element={<SetupPromptReviewPage isDarkMode={isDarkMode} />}
        />
        <Route
          path="/setup/prompt/:customerSlug"
          element={<SetupPromptReviewPage isDarkMode={isDarkMode} />}
        />
        <Route
          path="/setup/profile/:customerSlug/:workspaceSlug/:agentSlug"
          element={<SetupProfileReviewPage isDarkMode={isDarkMode} />}
        />
        <Route
          path="/setup/profile/:customerSlug"
          element={<SetupProfileReviewPage isDarkMode={isDarkMode} />}
        />
        <Route
          path="/onboarding"
          element={<Navigate to="/setup" replace />}
        />
        <Route
          path="/mode=dev"
          element={<Navigate to="/?mode=dev" replace />}
        />
        <Route
          path="/mode=prod"
          element={<Navigate to="/?mode=prod" replace />}
        />
        <Route
          path="/legacy-sally-landing"
          element={<Navigate to="/" replace />}
        />
        <Route
          path="/workspace/:slug"
          element={<WorkspaceRoute isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
        />
        <Route
          path="/dashboard/:customerSlug"
          element={<CustomerDashboardPage isDarkMode={isDarkMode} />}
        />
        <Route
          path="/agent/:customerSlug/:agentSlug"
          element={<CustomerAgentRoute isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
        />
        <Route path="/login" element={<LoginPage isDarkMode={isDarkMode} />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/c/:uuid"
          element={<ConversationRoute isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
        />
        <Route
          path="/admin"
          element={<AdminAreaRoute><AdminPanel isDarkMode={isDarkMode} /></AdminAreaRoute>}
        />
        <Route
          path="/admin/:customerSlug/:agentSlug"
          element={<AdminAreaRoute><AdminPanel isDarkMode={isDarkMode} /></AdminAreaRoute>}
        />
        <Route
          path="/superadmin"
          element={<AdminAreaRoute><AdminPanel isDarkMode={isDarkMode} superadminOnly /></AdminAreaRoute>}
        />
        <Route
          path="/admin/onboarding"
          element={<AdminAreaRoute><AdminOnboardingPage isDarkMode={isDarkMode} /></AdminAreaRoute>}
        />
        <Route
          path="/debug"
          element={<AdminAreaRoute><DebugPage isDarkMode={isDarkMode} /></AdminAreaRoute>}
        />
        <Route
          path="/:agentSlug"
          element={<PublicAgentRoute isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
        />
      </Routes>
    </div>
  );
}

export default App;
